/**
 * The password rule for accepting an XOP Reseller invitation — ONE
 * definition, used by the accept form and by the BFF that enforces it.
 *
 * Two copies of a password policy is one policy and one lie: the form
 * would tell people a rule the server does not apply, or refuse a password
 * the server would have taken. So the check lives here and both sides
 * import it. The server's verdict is still the one that counts — the client
 * runs it only so the feedback is immediate.
 *
 * WHAT IT DOES NOT DO. It is not a strength oracle and does not pretend to
 * be: no character-class quotas ("one uppercase, one symbol"), which push
 * people toward `Password1!` and are worse than length. The rules below are
 * the ones that actually remove guessable passwords —
 * NIST SP 800-63B §5.1.1.2's advice: a real length floor, and a blocklist
 * of the passwords attackers try first, including ones derived from the
 * account's own address.
 *
 * portal-api enforces its own 12-character floor
 * (`Auth::AcceptInvitation::MIN_PASSWORD_LENGTH`). This is that floor plus
 * the checks it does not make; it can only ever be stricter, never looser.
 */

/** Matches portal-api's floor. Raising it here alone would let this app
 * refuse a password the identity plane would accept — fine — but LOWERING
 * it would let one through that portal-api then rejects, and the token is
 * spent by then. Never set this below portal-api's value. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * An upper bound, because the password is hashed upstream and an unbounded
 * input is a free way to make a server do unbounded work. Generous enough
 * that no real passphrase hits it.
 */
export const MAX_PASSWORD_LENGTH = 128;

/**
 * Passwords that meet the length rule and are still worthless. Kept
 * deliberately short: this is not a substitute for a breach corpus, it is
 * the handful a person actually types when a form says "12 characters".
 * Compared case-insensitively, after stripping non-alphanumerics, so
 * `P@ssword1234` does not slip past `password1234`.
 */
const BLOCKLIST = [
  "password",
  "passw0rd",
  "password1",
  "password12",
  "password123",
  "password1234",
  "passwordpassword",
  "letmein",
  "letmeinplease",
  "welcome",
  "welcome123",
  "welcome1234",
  "qwertyuiop",
  "qwerty123456",
  "1234567890",
  "123456789012",
  "administrator",
  "iloveyou",
  "changeme",
  "changeme123",
  "trustno1",
  "xopnetworks",
  "xopanytime",
  "anytime123",
  "xopreseller",
  "reseller123",
];

export type PasswordProblem =
  | "too_short"
  | "too_long"
  | "contains_email"
  | "too_repetitive"
  | "sequential"
  | "common";

/** A failed check, with the sentence to show for it. */
export interface PasswordVerdict {
  ok: boolean;
  problem?: PasswordProblem;
  /** Ready to render. Says what to do, not merely what is wrong. */
  message?: string;
}

const MESSAGES: Record<PasswordProblem, string> = {
  too_short: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
  too_long: `Keep it under ${MAX_PASSWORD_LENGTH} characters.`,
  contains_email:
    "Don't build it from your email address — that's the first thing anyone guesses.",
  too_repetitive: "Too repetitive. A few unrelated words work better than one repeated.",
  sequential: "Runs like `123456` or `abcdef` are guessed in seconds.",
  common: "That's on every password-guessing list. Pick something less common.",
};

/** Strip everything an attacker's tooling would strip before comparing. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Undo the character swaps that make a blocklisted password *look* unlike
 * itself. `P@ssw0rd1234` is `password1234` to anything doing real guessing,
 * and folding these is the difference between a blocklist that works and
 * one that only catches the people who were not trying.
 *
 * Applied BEFORE `normalise` strips punctuation, or `@` and `!` would be
 * gone before they could be read as letters.
 */
const LEET: Record<string, string> = {
  "@": "a", "4": "a", "8": "b", "(": "c", "3": "e", "6": "g",
  "1": "i", "!": "i", "|": "i", "0": "o", "$": "s", "5": "s", "7": "t",
};

function defeat(value: string): string {
  return normalise(value.toLowerCase().replace(/[@48(36 1!|0$57]/g, (c) => LEET[c] ?? c));
}

/**
 * The length of the shortest block the password is just a repetition of, or
 * 0 when it is not periodic. `abababababab` is period 2 — a two-character
 * password wearing a costume, and no more resistant than `ab`.
 */
function repeatPeriod(value: string): number {
  for (let p = 1; p <= Math.floor(value.length / 3); p += 1) {
    let periodic = true;
    for (let i = p; i < value.length; i += 1) {
      if (value[i] !== value[i % p]) {
        periodic = false;
        break;
      }
    }
    if (periodic) return p;
  }
  return 0;
}

/** Longest run of one repeated character, e.g. "aaaa" -> 4. */
function longestRepeat(value: string): number {
  let best = 0;
  let run = 0;
  let prev = "";
  for (const ch of value) {
    run = ch === prev ? run + 1 : 1;
    prev = ch;
    if (run > best) best = run;
  }
  return best;
}

/** Longest ascending or descending consecutive run, e.g. "3456" / "dcba". */
function longestSequence(value: string): number {
  let best = 0;
  let run = 1;
  let direction = 0;
  for (let i = 1; i < value.length; i += 1) {
    const step = value.charCodeAt(i) - value.charCodeAt(i - 1);
    if ((step === 1 || step === -1) && (direction === 0 || step === direction)) {
      direction = step;
      run += 1;
    } else {
      run = step === 1 || step === -1 ? 2 : 1;
      direction = step === 1 || step === -1 ? step : 0;
    }
    if (run > best) best = run;
  }
  return Math.max(best, value.length > 0 ? 1 : 0);
}

/**
 * Judge a password. `email` is the invitee's own address — a password built
 * from it is the single most guessable thing they can choose, and only this
 * flow knows what that address is.
 */
export function checkInvitationPassword(password: string, email = ""): PasswordVerdict {
  const fail = (problem: PasswordProblem): PasswordVerdict => ({
    ok: false,
    problem,
    message: MESSAGES[problem],
  });

  if (password.length < MIN_PASSWORD_LENGTH) return fail("too_short");
  if (password.length > MAX_PASSWORD_LENGTH) return fail("too_long");

  const flat = normalise(password);

  // The whole address, and the local part on its own — `dana@acme.test`
  // makes `dana` the guess, not `dana@acme.test`.
  const local = normalise(email.split("@")[0] ?? "");
  const whole = normalise(email);
  if (local.length >= 3 && flat.includes(local)) return fail("contains_email");
  if (whole.length >= 3 && flat.includes(whole)) return fail("contains_email");

  // Half the password being one character means it is far shorter than it
  // looks: `aaaaaaaaaaaa` is a 1-character password wearing a costume.
  if (longestRepeat(password) >= Math.max(4, Math.ceil(password.length / 2))) {
    return fail("too_repetitive");
  }

  // …and so is a short block repeated: `abababababab` is `ab`.
  const period = repeatPeriod(password.toLowerCase());
  if (period > 0 && period <= 4) return fail("too_repetitive");

  if (longestSequence(password) >= 6) return fail("sequential");

  // Checked against BOTH the plain normalisation and the leet-folded one,
  // so neither `password1234` nor `P@ssw0rd1234` gets through — and a
  // blocklist entry that happens to contain digits still matches its own
  // literal form.
  const folded = defeat(password);
  if (BLOCKLIST.some((weak) => flat.includes(weak) || folded.includes(weak))) {
    return fail("common");
  }

  return { ok: true };
}

/**
 * A 0-3 score for the meter, and nothing more. It reports how much the
 * password has going for it beyond the rules above — length first, because
 * length is what actually buys resistance, then variety.
 *
 * Deliberately NOT a bits-of-entropy estimate: showing a precise-looking
 * number the maths does not support is worse than showing a coarse one.
 * A password that fails `checkInvitationPassword` always scores 0.
 */
export function passwordStrength(password: string, email = ""): 0 | 1 | 2 | 3 {
  if (!checkInvitationPassword(password, email).ok) return 0;

  let score = 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 24) score += 1;

  // Variety earns at most one step, and only below the length that already
  // earned it — so a long passphrase is never scored below a short jumble.
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) =>
    re.test(password),
  ).length;
  if (classes >= 3 && score < 3) score += 1;

  return Math.min(score, 3) as 0 | 1 | 2 | 3;
}

/** Copy for each meter step. Index is the score. */
export const PASSWORD_STRENGTH_LABELS = ["Not usable yet", "Good", "Strong", "Excellent"] as const;
